"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { parseAsString } from "nuqs";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { useListProjects } from "@/lib/api/generated";
import { formatShortDate } from "@/lib/date/format-short-date";
import { useAuth } from "@/features/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { type ActiveFilter } from "@/components/ui/filter-panel";
import {
  CreateProjectRoot,
  ProjectsArchiveConfirmModal,
  ProjectsManageProject,
  useCreateProjectModal,
  type ProjectCardData,
} from "@/features/projects";
import { ProjectsListPageHeader } from "@/features/projects/components/list/page-header";
import { ProjectsListToolbar } from "@/features/projects/components/list/toolbar";
import { ProjectsGrid } from "@/features/projects/components/list/grid";
import { ProjectsEmptyWelcome } from "@/features/projects/components/list/empty-welcome";
import { ProjectsEmptyNoResults } from "@/features/projects/components/list/empty-no-results";
import { ProjectsListFooter } from "@/features/projects/components/list/footer";
import {
  projectFiltersToQueryParams,
  type ProjectSelector,
} from "@/features/projects/components/list/filters";
import {
  ProjectsAcademyModal,
  useOpenProjectsAcademy,
} from "@/features/learn";

const PER_PAGE = 20;

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projectSelector, setProjectSelector] = useQuerySearchParams(
    {
      field: "project_selector",
      parser: parseAsString.withDefault("all"),
    },
  );
  const { openCreateProject } = useCreateProjectModal();
  const openProjectsAcademy = useOpenProjectsAcademy();

  const selector: ProjectSelector =
    projectSelector === "my" ? "my" : "all";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const handleSelectorChange = useCallback(
    (s: ProjectSelector) => {
      setPage(1);
      setProjectSelector(s === "all" ? null : s, {
        history: "replace",
        scroll: false,
      });
    },
    [setProjectSelector],
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleFiltersChange = useCallback((newFilters: ActiveFilter[]) => {
    setPage(1);
    setActiveFilters(newFilters);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setPage(1);
    setSearch(value);
  }, []);

  const filterParams = useMemo(
    () => projectFiltersToQueryParams(activeFilters),
    [activeFilters],
  );

  const { data, isLoading, isError, isPlaceholderData } = useListProjects(
    {
      search: debouncedSearch || undefined,
      status: filterParams.status as "active" | "archived" | undefined,
      status_not: filterParams.status_not as "active" | "archived" | undefined,
      visibility: filterParams.visibility as "private" | "shared" | undefined,
      visibility_not: filterParams.visibility_not as
        | "private"
        | "shared"
        | undefined,
      creator_id: filterParams.creator_id,
      creator_id_not: filterParams.creator_id_not,
      selector: selector === "my" ? "my" : undefined,
      page,
      per_page: PER_PAGE,
    },
    {
      query: {
        placeholderData: (prev) => prev,
      },
    },
  );

  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / PER_PAGE));

  const userName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email
    : "You";

  const projects: ProjectCardData[] = useMemo(() => {
    const mapped = (data?.projects || []).map((p) => {
      const creatorName =
        `${p.creator?.first_name || ""} ${p.creator?.last_name || ""}`.trim() ||
        p.creator?.email ||
        (p.is_owner ? userName : "Unknown");
      return {
        id: p.id,
        title: p.title,
        status: (p.status === "active"
          ? "Active"
          : "Archived") as ProjectCardData["status"],
        visibility: (p.access_level === "private"
          ? "Private"
          : "Shared") as ProjectCardData["visibility"],
        owner: creatorName,
        ownerAvatar: p.creator?.avatar_url || undefined,
        createdAt: `Created at ${formatShortDate(p.created_at)}`,
        jobCount: p.job_count ?? 0,
        coverImage: p.cover_image_url ?? null,
        selected: p.is_selected,
        isOwner: p.is_owner ?? false,
      };
    });
    return mapped.sort((a, b) => {
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;
      return 0;
    });
  }, [data?.projects, userName]);

  const hasFilters = !!(search || debouncedSearch || activeFilters.length > 0);
  const settled = (!isLoading && !isPlaceholderData) || isError;
  const showWelcomeState =
    settled && projects.length === 0 && !hasFilters;
  const showNoResultsState =
    settled && projects.length === 0 && !showWelcomeState;

  const clearFilters = useCallback(() => {
    handleFiltersChange([]);
    setPage(1);
    setSearch("");
  }, [handleFiltersChange]);

  return (
    <main
      aria-labelledby="projects-page-heading"
      className="flex h-full flex-1 flex-col bg-surface-primary"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ProjectsListPageHeader
          headingId="projects-page-heading"
          onCreate={() => openCreateProject()}
        />

        <div role="status" aria-live="polite" className="sr-only">
          {settled
            ? `${data?.total ?? projects.length} project${(data?.total ?? projects.length) === 1 ? "" : "s"} found`
            : ""}
        </div>

        <ProjectsListToolbar
          selector={selector}
          onSelectorChange={handleSelectorChange}
          search={search}
          onSearchChange={handleSearchChange}
          activeFilters={activeFilters}
          onFiltersChange={handleFiltersChange}
          filterOpen={filterOpen}
          onFilterOpenChange={setFilterOpen}
        />

        <ScrollArea
          className="min-h-0 flex-1 bg-surface-primary"
          viewportClassName="[&>div]:h-full"
          contentClassName="min-h-full"
          constrainContentWidth
        >
          {isLoading || isPlaceholderData ? (
            <ProjectsGrid projects={[]} loading />
          ) : showWelcomeState ? (
            <ProjectsEmptyWelcome
              onCreate={() => openCreateProject()}
              onLearnMore={openProjectsAcademy}
            />
          ) : showNoResultsState ? (
            <ProjectsEmptyNoResults
              hasFilters={activeFilters.length > 0}
              onClearFilters={clearFilters}
            />
          ) : (
            <ProjectsGrid projects={projects} loading={false} />
          )}
        </ScrollArea>

        {!showWelcomeState && (
          <ProjectsListFooter
            total={data?.total || projects.length}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            loading={isLoading}
            paginationDisabled={isPlaceholderData}
          />
        )}
      </div>

      <ProjectsManageProject />

      <ProjectsArchiveConfirmModal />

      <ProjectsAcademyModal />
      <CreateProjectRoot />
    </main>
  );
}
